
import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Mic, Send, Bot, User, X, Minimize2, Maximize2, Volume2, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import { edgeFunctionManager } from "@/lib/edgeFunctionManager";
import { errorHandler } from "@/lib/errorHandler";
import { speakWithElevenLabs } from "@/lib/tts";
interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  metadata?: any;
}

interface AIConciergeProps {
  isOpen: boolean;
  onToggle: () => void;
}

export const AIConcierge = ({ isOpen, onToggle }: AIConciergeProps) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [streamingMessage, setStreamingMessage] = useState("");
  const [speakingId, setSpeakingId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);

  // Fetch user context for AI
  const { data: userContext } = useQuery({
    queryKey: ["ai-context", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      // Fetch comprehensive user data for AI context
      const [profile, events, venues, friends] = await Promise.all([
        supabase.from('users').select('*').eq('id', user.id).single(),
        supabase.from('events').select('*').limit(10),
        supabase.from('venues').select('*').limit(10),
        supabase.from('followers').select('*').eq('follower_id', user.id).limit(20)
      ]);
      
      return {
        profile: profile.data,
        recentEvents: events.data || [],
        nearbyVenues: venues.data || [],
        friends: friends.data || [],
        preferences: {
          musicGenres: profile.data?.music_preferences || [],
          eventTypes: profile.data?.event_preferences || [],
          locations: profile.data?.location_preferences || []
        }
      };
    },
    enabled: !!user?.id && isOpen,
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Welcome message
      setMessages([{
        id: '1',
        role: 'assistant',
        content: `🌟 Hey there! I'm your personal nightlife concierge. I know your preferences, favorite venues, and can help you discover amazing events tonight. What are you in the mood for?`,
        timestamp: new Date()
      }]);
    }
  }, [isOpen, messages.length]);

  // Voice recognition setup
  useEffect(() => {
    let SpeechRecognitionClass:
      | typeof window.SpeechRecognition
      | typeof window.webkitSpeechRecognition
      | undefined;

    if (typeof window !== "undefined") {
      // Assign browser SpeechRecognition object, with fallback
      SpeechRecognitionClass =
        window.SpeechRecognition || window.webkitSpeechRecognition;
    }

    if (SpeechRecognitionClass) {
      recognitionRef.current = new SpeechRecognitionClass();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = "en-US";

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInput(transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = () => {
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, []);

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      setIsListening(true);
      recognitionRef.current.start();
    }
  };

  const handleSpeak = async (messageId: string, text: string) => {
    try {
      setSpeakingId(messageId);
      await speakWithElevenLabs(text);
    } catch (e) {
      console.error('TTS error', e);
    } finally {
      setSpeakingId(null);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = input;
    setInput("");
    setIsLoading(true);
    setStreamingMessage("");

    try {
      // Call modern AI service with thread support
      const response = await supabase.functions.invoke('ai-concierge', {
        body: {
          message: currentInput,
          context: userContext,
          conversationHistory: messages.slice(-5),
          threadId: threadId
        }
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      const { data } = response;
      
      // Update thread ID for persistent conversation
      if (data.threadId && !threadId) {
        setThreadId(data.threadId);
      }
      
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.response,
        timestamp: new Date(),
        metadata: data.suggestions
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('AI Concierge error:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: "Sorry, I'm having trouble connecting right now. Please try again! 🌟",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    }

    setIsLoading(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: 20 }}
      className={`fixed bottom-4 right-4 z-50 ${
        isMinimized ? 'w-80 h-16' : 'w-96 h-[600px]'
      } transition-all duration-300`}
    >
      <Card className="cyber-glass h-full flex flex-col overflow-hidden border-neon-purple/30">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neon-purple/20">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Avatar className="w-8 h-8 ring-2 ring-neon-purple/50">
                <AvatarFallback className="bg-gradient-to-br from-neon-purple to-neon-pink text-white">
                  <Bot className="w-4 h-4" />
                </AvatarFallback>
              </Avatar>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-neon-green rounded-full animate-pulse"></div>
            </div>
            <div>
              <h3 className="font-semibold text-white text-sm">Noche AI</h3>
              <p className="text-xs text-gray-400">Your nightlife concierge</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMinimized(!isMinimized)}
              className="w-8 h-8 p-0 text-gray-400 hover:text-white"
            >
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggle}
              className="w-8 h-8 p-0 text-gray-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <AnimatePresence>
                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex gap-3 ${
                      message.role === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    {message.role === 'assistant' && (
                      <Avatar className="w-6 h-6 mt-1 ring-1 ring-neon-purple/30">
                        <AvatarFallback className="bg-gradient-to-br from-neon-purple to-neon-pink text-white text-xs">
                          <Bot className="w-3 h-3" />
                        </AvatarFallback>
                      </Avatar>
                    )}
                    
                    <div
                      className={`max-w-[80%] p-3 rounded-2xl ${
                        message.role === 'user'
                          ? 'bg-gradient-to-r from-neon-purple to-neon-pink text-white'
                          : 'cyber-glass border border-neon-purple/20 text-gray-100'
                      }`}
                    >
                      <p className="text-sm leading-relaxed">{message.content}</p>
                      <p className="text-xs opacity-60 mt-1">
                        {message.timestamp.toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </p>
                      {message.role === 'assistant' && (
                        <div className="mt-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-gray-300 hover:text-white hover:bg-white/5"
                            onClick={() => handleSpeak(message.id, message.content)}
                          >
                            {speakingId === message.id ? (
                              <>
                                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                                Playing
                              </>
                            ) : (
                              <>
                                <Volume2 className="w-3.5 h-3.5 mr-1" />
                                Listen
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>

                    {message.role === 'user' && (
                      <Avatar className="w-6 h-6 mt-1 ring-1 ring-neon-pink/30">
                        <AvatarFallback className="bg-gradient-to-br from-neon-pink to-neon-orange text-white text-xs">
                          <User className="w-3 h-3" />
                        </AvatarFallback>
                      </Avatar>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
              
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex gap-3 justify-start"
                >
                  <Avatar className="w-6 h-6 mt-1 ring-1 ring-neon-purple/30">
                    <AvatarFallback className="bg-gradient-to-br from-neon-purple to-neon-pink text-white text-xs">
                      <Bot className="w-3 h-3" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="cyber-glass border border-neon-purple/20 p-3 rounded-2xl">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-neon-purple rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-neon-pink rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-neon-blue rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </motion.div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-neon-purple/20">
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask me about events, venues, or planning your night..."
                  className="flex-1 bg-black/50 border-neon-purple/30 text-white placeholder-gray-400 focus:border-neon-purple"
                  disabled={isLoading}
                />
                <Button
                  onClick={startListening}
                  disabled={isLoading || isListening}
                  variant="outline"
                  size="sm"
                  className={`px-3 border-neon-purple/30 hover:border-neon-purple ${
                    isListening ? 'bg-neon-purple/20 border-neon-purple' : ''
                  }`}
                >
                  <Mic className={`w-4 h-4 ${isListening ? 'text-neon-purple' : 'text-gray-400'}`} />
                </Button>
                <Button
                  onClick={sendMessage}
                  disabled={!input.trim() || isLoading}
                  className="px-3 bg-gradient-to-r from-neon-purple to-neon-pink hover:from-neon-pink hover:to-neon-purple"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </motion.div>
  );
};
